/**
 * AppICEMAP — ReportList Component
 * Composes SearchBar → CategoryPills → ReportCard[] → EmptyState
 * with store subscriptions, filtering, and sorting.
 *
 * Tag: <report-list>
 *
 * Dispatches:
 *   report-list:card-click       → { reportId }
 *   report-list:ver-detalle      → { reportId }
 *   report-list:quick-confirm    → { reportId }
 *   report-list:quick-deny       → { reportId }
 */

import { state, subscribe, dispatch } from "../../store/index.js";

export class ReportList extends HTMLElement {
	constructor() {
		super();
		this._reports = [];
		this._categories = [];
		this._filters = { selectedCategories: [], searchText: "" };
		this._userLocation = null;
		this._unsubs = [];
	}

	connectedCallback() {
		this._render();

		// Subscribe to store changes
		this._unsubs.push(
			subscribe("reports", (reports) => {
				this._reports = reports || [];
				this._renderCards();
			}),
		);

		this._unsubs.push(
			subscribe("filters", (filters) => {
				this._filters = filters;
				this._renderCards();
			}),
		);

		this._unsubs.push(
			subscribe("categories", (cats) => {
				this._categories = cats || [];
				this._updatePills();
			}),
		);

		// Watch ui.userLocation
		this._unsubs.push(
			subscribe("ui", (ui) => {
				if (ui.userLocation !== this._userLocation) {
					this._userLocation = ui.userLocation;
					// With a radius active the list membership itself depends on
					// where you are, so a full re-render is needed
					if (this._filters?.radiusKm) this._renderCards();
					else this._updateDistances();
				}
			}),
		);

		// Initial load
		this._reports = Array.isArray(state.reports) ? state.reports : [];
		this._categories = Array.isArray(state.categories) ? state.categories : [];
		this._filters = state.filters || { selectedCategories: [], searchText: "" };
		this._userLocation = state.ui?.userLocation || null;
		this._renderCards();
		this._updatePills();
	}

	disconnectedCallback() {
		for (const unsub of this._unsubs) unsub();
		this._unsubs = [];
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		// Search lives in the app shell (above the map) in the Nocturne layout;
		// this component owns the category pills + the scrollable card list.
		this.innerHTML = `
      <category-pills></category-pills>
      <div class="report-list"></div>
    `;

		this._pills = this.querySelector("category-pills");
		this._listEl = this.querySelector(".report-list");

		// Category pills → store
		if (this._pills) {
			this._pills.addEventListener("category-pills:toggle", (e) => {
				const { slug, selected } = e.detail;
				if (slug === "all") {
					dispatch("CLEAR_CATEGORY_FILTERS");
				} else {
					dispatch("TOGGLE_CATEGORY_FILTER", slug);
				}
			});
		}
	}

	/* ── Cards ─────────────────────────────────────────────────────────────── */

	_renderCards() {
		if (!this._listEl) return;

		// Filter
		const filtered = this._filterReports();
		// Sort: nearest first by default
		const sorted = this._sortReports(filtered);

		// Update pills counts
		this._updatePills();

		if (sorted.length === 0) {
			this._listEl.innerHTML = "";
			const empty = document.createElement("empty-state");
			const hasFilters =
				this._filters.selectedCategories.length > 0 ||
				(this._filters.searchText && this._filters.searchText.trim());

			if (this.hiddenByRadius > 0) {
				const km = this._filters.radiusKm;
				empty.setAttribute("icon", "crosshair");
				empty.setAttribute("title", `Nada a menos de ${km} km`);
				empty.setAttribute(
					"message",
					`Hay ${this.hiddenByRadius} reporte${this.hiddenByRadius === 1 ? "" : "s"} más lejos. Amplía el radio en tu perfil.`,
				);
			} else if (hasFilters) {
				empty.setAttribute("icon", "faders");
				empty.setAttribute("title", "Sin resultados");
				empty.setAttribute(
					"message",
					"Intenta con otros filtros o términos de búsqueda",
				);
			} else if (this._reports.length === 0) {
				empty.setAttribute("icon", "map-pin");
				empty.setAttribute("title", "No hay reportes activos en esta zona");
				empty.setAttribute("message", "Mueve el mapa o cambia los filtros");
			} else {
				empty.setAttribute("icon", "faders");
				empty.setAttribute("title", "Sin resultados");
				empty.setAttribute("message", "Intenta con otros filtros");
			}
			this._listEl.appendChild(empty);
			return;
		}

		// Clear and rebuild
		this._listEl.innerHTML = "";
		const fragment = document.createDocumentFragment();

		for (const report of sorted) {
			const card = document.createElement("report-card");
			card.report = report;
			card.userLocation = this._userLocation;
			card.isSelected = state.ui?.selectedReportId === report.id;

			// Forward events — clicking a card selects it and flies the map to it
			card.addEventListener("report-card:click", (e) => {
				dispatch("SELECT_REPORT", e.detail.reportId);
			});

			card.addEventListener("report-card:ver-detalle", (e) => {
				this.dispatchEvent(
					new CustomEvent("report-list:ver-detalle", {
						detail: { reportId: e.detail.reportId },
						bubbles: true,
					}),
				);
			});

			fragment.appendChild(card);
		}

		this._listEl.appendChild(fragment);
	}

	/**
	 * How many reports the radius filter is currently hiding.
	 * @returns {number}
	 */
	get hiddenByRadius() {
		return this._hiddenByRadius || 0;
	}

	_filterReports() {
		let reports = this._reports;

		// Category filter
		if (
			this._filters.selectedCategories &&
			this._filters.selectedCategories.length > 0
		) {
			reports = reports.filter(
				(r) =>
					r.category &&
					this._filters.selectedCategories.includes(r.category.slug),
			);
		}

		// Nearby radius: only reports within N km of you. Needs a GPS fix — with
		// no location there is nothing to measure from, so the filter is skipped
		// rather than emptying the list.
		this._hiddenByRadius = 0;
		const radiusKm = this._filters.radiusKm;
		if (radiusKm && this._userLocation) {
			const before = reports.length;
			reports = reports.filter((r) => {
				const d = this._calcDistance(r);
				return d === null || d <= radiusKm;
			});
			this._hiddenByRadius = before - reports.length;
		}

		// Search text
		if (this._filters.searchText && this._filters.searchText.trim()) {
			const query = this._filters.searchText.toLowerCase().trim();
			reports = reports.filter(
				(r) =>
					(r.title && r.title.toLowerCase().includes(query)) ||
					(r.description && r.description.toLowerCase().includes(query)) ||
					(r.address && r.address.toLowerCase().includes(query)),
			);
		}

		return reports;
	}

	_sortReports(reports) {
		// Sort by distance from user (nearest first), then by time (newest first)
		return [...reports].sort((a, b) => {
			const distA = this._calcDistance(a) ?? Infinity;
			const distB = this._calcDistance(b) ?? Infinity;
			if (distA !== distB) return distA - distB;
			return (
				new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
			);
		});
	}

	_calcDistance(report) {
		if (!this._userLocation || !report.location?.coordinates) return null;
		const [lng, lat] = report.location.coordinates;
		const [uLat, uLng] = this._userLocation;

		const R = 6371;
		const dLat = toRad(lat - uLat);
		const dLng = toRad(lng - uLng);
		const a =
			Math.sin(dLat / 2) ** 2 +
			Math.cos(toRad(uLat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
		return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	}

	_updatePills() {
		if (!this._pills) return;

		this._pills.categories = this._categories;
		this._pills.selected = this._filters.selectedCategories || [];

		// Compute counts
		const counts = {};
		for (const r of this._reports) {
			if (r.category?.slug) {
				counts[r.category.slug] = (counts[r.category.slug] || 0) + 1;
			}
		}
		this._pills.reportCounts = counts;
	}

	_updateDistances() {
		const cards = this._listEl?.querySelectorAll("report-card");
		if (!cards) return;
		for (const card of cards) {
			card.userLocation = this._userLocation;
		}
	}
}

function toRad(deg) {
	return deg * (Math.PI / 180);
}

customElements.define("report-list", ReportList);
